/**
 * Bytes on a JSON wire.
 *
 * A protobuf `bytes` field crosses the gateway as standard base64 (protojson), so a file upload
 * is `Uint8Array → base64 string` on the way out and the reverse on the way back. Two functions
 * rather than `btoa` at each call site, because `btoa` takes a Latin-1 STRING and a CSV with an
 * umlaut fed to it directly is a silent corruption — every byte has to be widened to a code
 * point first, which is what the loop below does.
 */
export function encodeBytes(bytes: Uint8Array): string {
  let binary = '';
  // Chunked so a large XLSX does not exceed the argument limit of `String.fromCharCode`.
  const CHUNK = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + CHUNK));
  }
  return btoa(binary);
}

export function decodeBytes(encoded: string): Uint8Array {
  // protojson accepts either alphabet on input and emits the standard one; a gateway that
  // answered with URL-safe characters would otherwise fail to decode here.
  const standard = encoded.replaceAll('-', '+').replaceAll('_', '/');
  const binary = atob(standard);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** A CSV the console produced or received, as UTF-8 bytes — the common case of a file upload. */
export const utf8Bytes = (text: string): Uint8Array => new TextEncoder().encode(text);

export const utf8Text = (bytes: Uint8Array): string => new TextDecoder().decode(bytes);
